<template>
  <li class="col-span-1">
    <div
      class="group w-full h-full flex flex-col bg-white rounded-xl border border-untitled-gray-200 shadow-sm hover:shadow-md hover:border-untitled-gray-300 transition-all duration-200 overflow-hidden"
      :id="`playlist_card_${playlistId}`"
    >
      <!-- Thumbnail -->
      <div
        class="relative w-full aspect-video cursor-pointer overflow-hidden bg-untitled-gray-100"
        @click="handleClickToPlaylist"
      >
        <img
          v-lazy="{
            src: thumbnail,
            error:
              'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
            loading:
              'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
          }"
          class="object-cover object-center w-full h-full transition-transform duration-300 group-hover:scale-105"
        />
        <div
          class="absolute inset-0 flex justify-center items-center bg-untitled-gray-900/0 group-hover:bg-untitled-gray-900/30 transition-colors duration-200"
        >
          <PlayIcon
            v-if="totalProgress !== 100"
            class="w-12 h-12 text-white opacity-0 group-hover:opacity-90 transition-opacity duration-200"
          />
          <CheckCircleIcon v-else class="w-12 h-12 text-white opacity-80" />
        </div>

        <!-- Top-left badge -->
        <div
          v-if="dueDate && !featured"
          class="absolute top-2 left-2 bg-untitled-gray-800/90 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"
        >
          Assigned to you
        </div>
        <div
          v-else-if="featured"
          class="absolute top-2 left-2 bg-untitled-gray-800/90 text-white text-[11px] font-semibold uppercase px-2.5 py-1 rounded-full"
        >
          Learning Path
        </div>

        <!-- Top-right badge -->
        <div
          v-if="totalProgress >= 100 && !featured"
          class="absolute top-2 right-2 flex items-center gap-1 font-semibold text-green-700 text-[11px] bg-green-100 px-2.5 py-1 rounded-full"
        >
          Completed
        </div>
        <div
          v-else-if="featured"
          class="absolute top-2 right-2 flex items-center gap-1 font-bold uppercase text-red-600 text-[11px] bg-red-100 px-2.5 py-1 rounded-full"
        >
          <FireIcon class="w-3.5 h-3.5" />
          Featured
        </div>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-3 p-4 flex-1">
        <el-tooltip
          :content="wrapTooltipContent(title)"
          effect="dark"
          :raw-content="true"
          placement="top-start"
        >
          <h1
            class="text-base font-semibold text-untitled-gray-800 line-clamp-1 cursor-pointer"
            @click="handleClickToPlaylist"
          >
            {{ title }}
          </h1>
        </el-tooltip>

        <!-- Progress -->
        <div class="flex flex-col gap-1.5">
          <div class="flex items-baseline gap-1">
            <span class="text-sm font-bold text-emerald-600"
              >{{ totalProgress }}%</span
            >
            <span class="text-sm text-untitled-gray-500">Completed</span>
          </div>
          <div
            class="w-full h-2 rounded-full bg-untitled-gray-100 overflow-hidden"
          >
            <div
              class="h-full rounded-full bg-teal-700 transition-all duration-300"
              :style="{ width: `${totalProgress}%` }"
            />
          </div>
        </div>

        <!-- Meta -->
        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-untitled-gray-500"
        >
          <div v-if="videoContent.length > 0" class="flex items-center gap-1">
            <VideoCameraIcon class="w-4 h-4 text-untitled-gray-400" />
            <span class="font-medium">
              {{ filteredTotalVideosCompleted }} Videos
            </span>
          </div>
          <div v-if="textContent.length > 0" class="flex items-center gap-1">
            <DocumentTextIcon class="w-4 h-4 text-untitled-gray-400" />
            <span class="font-medium">
              {{ filteredTotalTextUnitsCompleted }} Text Units
            </span>
          </div>
          <el-tooltip
            v-if="dueDate"
            effect="dark"
            content="Expected date and time of completion of the playlist"
          >
            <div class="flex items-center gap-1">
              <CalendarIcon class="w-4 h-4 text-untitled-gray-400" />
              {{ formatDate(dueDate, "MM/DD/YYYY") }}
            </div>
          </el-tooltip>
        </div>

        <!-- Actions -->
        <div class="mt-auto pt-1 flex flex-wrap gap-2">
          <template v-if="!showOpenPlaylist">
            <AssignToUser
              v-if="contents.length"
              :playlist-id="playlistId"
              isPlaylist
              v-slot="{ clickAssign }"
              title="Assign Playlist"
              label="Assign Playlist"
            >
              <button
                type="button"
                @click="clickAssign"
                v-has-restrict-learner
                class="flex-1 min-w-36 inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-untitled-gray-700 bg-untitled-gray-100 hover:bg-untitled-gray-200 transition-colors"
              >
                <LinkIcon class="w-4 h-4" />
                <span class="truncate">Assign Playlist</span>
              </button>
            </AssignToUser>
            <button
              v-if="isSuperAdmin || isOwner"
              type="button"
              @click="handleClickEdit"
              class="flex-1 min-w-36 inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-untitled-gray-700 bg-untitled-gray-100 hover:bg-untitled-gray-200 transition-colors"
            >
              <PencilAltIcon class="w-4 h-4" />
              <span class="truncate">Edit Playlist</span>
            </button>
          </template>
          <button
            v-else
            type="button"
            @click="handleClickToPlaylist"
            class="w-full inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-white bg-untitled-gray-800 hover:bg-untitled-gray-900 transition-colors"
          >
            <component :is="showOpenPlaylist ? CursorClickIcon : ArrowsExpandIcon" class="w-4 h-4" />
            {{ buttonLabel }}
          </button>
        </div>
      </div>
    </div>
  </li>
</template>

<script setup>
import { defineProps, computed } from "vue";
import { dayjs, ElTooltip } from "element-plus";
import { wrapTooltipContent } from "@/composable/wrapTooltipContent";
import {
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PlayIcon,
  VideoCameraIcon,
  PencilAltIcon,
  ArrowsExpandIcon,
  CursorClickIcon,
  FireIcon,
  LinkIcon,
} from "@heroicons/vue/solid";
import AssignToUser from "@/components/utilities/AssignToUser.vue";
import { useRouter } from "vue-router";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  thumbnail: {
    type: [String, null],
    required: true,
  },
  totalProgress: {
    type: Number,
    required: true,
  },
  dueDate: {
    type: [String, null],
    required: true,
  },
  totalVideosCompleted: {
    type: [Number, null],
    required: true,
  },
  pathId: {
    type: [Number, null],
    required: true,
  },
  totalVideoDuration: {
    type: [String, null],
    required: true,
  },
  totalVideos: {
    type: [Number, null],
    required: true,
  },
  contents: {
    type: [Array, null],
    required: true,
    default: () => [],
  },
  isOwner: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: [String, null],
    default: "",
  },
  type: {
    type: [String, null],
    default: "",
  },
  isSuperAdmin: {
    type: Boolean,
    default: false,
  },
  isCompanyAdmin: {
    type: Boolean,
    default: false,
  },
  isManager: {
    type: Boolean,
    default: false,
  },
  isLearner: {
    type: Boolean,
    default: false,
  },
  showOpenPlaylist: {
    type: Boolean,
    default: false,
  },
  featured: {
    type: Boolean,
  },
});

const router = useRouter();

const title = computed(() => props.title);
const thumbnail = computed(() => props.thumbnail);
const totalProgress = computed(() => props.totalProgress);
const dueDate = computed(() => props.dueDate);
const isSuperAdmin = computed(() => props.isSuperAdmin);
const isOwner = computed(() => props.isOwner);
const type = computed(() => props.type);
const playlistId = computed(() => props.pathId);
const contents = computed(() => props.contents);
const showOpenPlaylist = computed(() => props.showOpenPlaylist);
const featured = computed(() => props.featured);

const videoContent = computed(() => {
  return props.contents.filter(
    (content) =>
      content.type == "video" ||
      content.type == "custom" ||
      content.type == "scorm" ||
      content.type == "s3"
  );
});

const textContent = computed(() => {
  return props.contents.filter((content) => content.type == "text");
});

const buttonLabel = computed(() => {
  if (totalProgress.value <= 0 && totalProgress.value !== 100) {
    return "Start Learning";
  } else {
    return "Open Playlist";
  }
});

const filteredTotalVideosCompleted = computed(() => {
  return `${
    videoContent.value.filter((video) => video.is_completed == true)?.length
  }/${videoContent.value.length}`;
});

const filteredTotalTextUnitsCompleted = computed(() => {
  return `${
    textContent.value.filter((text) => text.is_completed == true)?.length
  }/${textContent.value.length}`;
});

const handleClickToPlaylist = () => {
  if (!contents.value.length) {
    handleClickEdit();
  }

  const currentUnit = contents.value.find((content) => !content.is_completed);
  let unitId = null;

  if (currentUnit === undefined) {
    unitId = props.contents[0].id;
  } else {
    unitId = currentUnit.id;
  }

  if (props.type === "custom") {
    router.push({
      name: "Custom Playlist Unit Page",
      params: {
        id: unitId,
        playlistId: playlistId.value,
      },
    });
  } else {
    router.push({
      name: "units",
      params: {
        id: unitId,
        playlistId: playlistId.value,
      },
    });
  }
};

const formatDate = (value, format) => {
  return dayjs(value).format(format);
};

const handleClickEdit = () => {
  let route = "";
  if (type.value === "custom") {
    route = `/custom-playlist/${props.pathId}/edit`;
  } else {
    route = "/edit/playlist/" + props.pathId;
  }

  router.push(route);
};
</script>

<style lang="css" scoped></style>
